import { sendDataLayer } from "jiffy-analytics";
import { sendSocialMediaConversionApiData } from "components/jiffy/product-details-quantity-form/analytics";
import { generateRandomString } from "common/utils/helpers/random";

export const PDP_PAGE_VISIT = "PDPPageVisit";
export const ADD_TO_CART = "AddToCart";
export const VIEW_CONTENT = "ViewContent";

const $getPixelContainer = () => document.querySelector(".js-pixel-container");

const getFbUserData = () => {
  const $pixelContainer = $getPixelContainer();
  if (!$pixelContainer || !$pixelContainer.dataset.fbUserData) {
    return {};
  }

  try {
    return JSON.parse($pixelContainer.dataset.fbUserData);
  } catch (error) {
    console.error("Failed to parse fbUserData:", error);
    return {};
  }
};

const getOrderEmail = () => {
  const $pixelContainer = $getPixelContainer();
  if (!$pixelContainer || !$pixelContainer.dataset.orderEmail) {
    return "";
  }

  try {
    return $pixelContainer.dataset.orderEmail;
  } catch (error) {
    console.error("Failed to parse orderEmail:", error);
    return "";
  }
};

const buildFbContent = ({ product, price, contentIds, composition }) => ({
  content_ids: contentIds,
  content_name: `Custom Printed ${product.brand} ${product.catalogNumber} ${composition.colorName}`,
  content_type: "product",
  value: price,
  currency: product.currency,
  user_data: getFbUserData()
});

const buildEcommerceItem = ({ product, variant, price, composition }) => ({
  item_id: variant.sku,
  name: `Custom Printed ${product.brand} ${product.catalogNumber} ${composition.colorName}`,
  item_brand: product.brand,
  item_category: product.googleAdsCategory,
  price
});

const buildEcommerceData = (product, composition, price) => ({
  currency: product.currency,
  value: price,
  items: composition.variants.map(variant =>
    buildEcommerceItem({ product, variant, price, composition })
  )
});

const calculateAddToCartValue = (productPrice, quantity) => {
  const price = parseFloat(productPrice);
  const qty = parseInt(quantity, 10);

  if (Number.isNaN(price) || Number.isNaN(qty)) {
    return 0;
  }

  const total = price * qty;
  return parseFloat(total.toFixed(2));
};

export const trackPDPPageVisit = (product, composition, price) => {
  const fbUserData = getFbUserData();
  const userIdentifier = fbUserData.user_identifier || generateRandomString();
  const viewContentEventId = `VC-${userIdentifier}-${Date.now()}`;
  const pageViewEventId = `PV-${userIdentifier}-${Date.now()}`;

  const items = composition.variants.map(variant => ({
    product_id: variant.sku
  }));
  const contentIds = composition.variants.map(variant => variant.sku);

  const fbContent = buildFbContent({ product, price, contentIds, composition });

  const itemsViewed = composition.variants.map(variant => ({
    name: `Custom Printed ${product.brand} ${product.catalogNumber} ${composition.colorName}`,
    id: variant.sku,
    brand: product.brand,
    price,
    google_business_vertical: "retail"
  }));

  sendDataLayer({
    event: PDP_PAGE_VISIT,
    items,
    msAdProductIds: contentIds,
    pageViewEventId,
    viewContentEventId,
    itemsViewed,
    viewItemValue: price,
    fbContent,
    ecommerce: buildEcommerceData(product, composition, price)
  });

  sendSocialMediaConversionApiData(
    fbContent,
    composition.colorName,
    viewContentEventId,
    pageViewEventId,
    VIEW_CONTENT
  );
};

export const trackAddToCart = ({ product, composition, quantity, price, variantId }) => {
  const contentIds = composition.variants.map(variant => variant.sku);
  const itemsAdded = composition.variants
    .filter(variant => variant.id === variantId)
    .map(variant => ({
      id: variant.sku,
      name: `Custom Printed ${product.brand} ${product.catalogNumber} ${composition.colorName}`,
      brand: product.brand,
      price,
      currency: product.currency,
      sku: variant.sku,
      quantity
    }));

  sendDataLayer({
    event: ADD_TO_CART,
    itemsAdded,
    email: getOrderEmail(),
    addToCartValue: calculateAddToCartValue(price, quantity),
    fbContent: buildFbContent({ product, price, contentIds, composition }),
    ecommerce: buildEcommerceData(product, composition, price)
  });
};
